import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Award, CalendarCheck, ChevronDown, ChevronUp } from "lucide-react";

const PortalSettings = ({ account, onAccountUpdate }) => {
  const [open, setOpen] = useState(false);
  const lp = account?.loyalty_program || {};
  const [enabled, setEnabled] = useState(lp.enabled || false);
  const [threshold, setThreshold] = useState(lp.threshold || 3);
  const [reward, setReward] = useState(lp.reward || "");
  const [reservationUrl, setReservationUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const saveLoyalty = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/restaurant-portal/loyalty`,
        { enabled, threshold: Number(threshold), reward },
        { withCredentials: true }
      );
      toast.success(enabled ? "Treueprogramm aktiviert!" : "Treueprogramm gespeichert");
      onAccountUpdate?.({ ...account, loyalty_program: response.data.loyalty_program });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const saveReservationUrl = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/restaurant-portal/reservation-url`,
        { url: reservationUrl },
        { withCredentials: true }
      );
      toast.success(`Reservierungs-Link für ${response.data.restaurant_name} gespeichert!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border mb-8" data-testid="portal-settings-card">
      <CardHeader className="cursor-pointer" onClick={() => setOpen(!open)} data-testid="portal-settings-toggle">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            Treueprogramm & Reservierung
            {lp.enabled && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-600 font-normal">aktiv</span>
            )}
          </span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="font-medium">Stammgast-Treueprogramm</Label>
                <p className="text-sm text-muted-foreground">
                  Gäste erhalten automatisch eine Benachrichtigung mit Ihrer Belohnung, sobald sie die Besuchsanzahl erreichen.
                </p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="loyalty-enabled-toggle" />
            </div>
            {enabled && (
              <div className="grid gap-4 sm:grid-cols-[140px,1fr]">
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Ab Besuch Nr.</Label>
                  <Input
                    type="number" min={2} max={20}
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    className="bg-transparent border-border"
                    data-testid="loyalty-threshold-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Belohnung</Label>
                  <Input
                    placeholder="z.B. Ein Aperitif aufs Haus 🥂"
                    value={reward}
                    onChange={(e) => setReward(e.target.value)}
                    maxLength={300}
                    className="bg-transparent border-border"
                    data-testid="loyalty-reward-input"
                  />
                </div>
              </div>
            )}
            <Button onClick={saveLoyalty} disabled={saving} className="bg-primary text-primary-foreground" data-testid="loyalty-save-button">
              Treueprogramm speichern
            </Button>
          </div>

          <div className="border-t border-border pt-6 space-y-3">
            <Label className="font-medium flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-primary" />
              Reservierungs-Link
            </Label>
            <p className="text-sm text-muted-foreground">
              Hinterlegen Sie Ihren Reservierungs-Link (z. B. OpenTable, eigene Website) –
              er wird allen Étoile-Nutzern direkt bei Ihrem Restaurant angezeigt.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="https://www.opentable.de/r/ihr-restaurant"
                value={reservationUrl}
                onChange={(e) => setReservationUrl(e.target.value)}
                className="bg-transparent border-border flex-1"
                data-testid="reservation-url-input"
              />
              <Button onClick={saveReservationUrl} disabled={saving || !reservationUrl.trim()} variant="outline" className="border-border" data-testid="reservation-url-save">
                Speichern
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default PortalSettings;
